import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { FilePortalDataService } from '../file-portal-dataservice';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { FilePortalService } from 'src/app/shared/api/services';
import { ErrorHandlerComponent, buttontypes } from 'src/app/shared/components/error-handler/error-handler.component';

@Component({
  selector: 'client-files-upload',
  templateUrl: './client-files-upload.component.html',
  styleUrls: ['./client-files-upload.component.scss']
})
export class ClientFilesUploadComponent implements OnInit {
  // #region Public Views Elements
  @Input() year: number;
  @Input() month: number;
  @Input() clientId: string;
  @Output() update: EventEmitter<any> = new EventEmitter();

  @ViewChild('fileInput', { static: true }) fileEl: ElementRef;
  @ViewChild(ErrorHandlerComponent, { static: true }) errorHandlerComponent: ErrorHandlerComponent;
  // #endregion

  // #region Public Properties
  invoiceNamingConvention: RegExp = /^([0-9]{4})_([0-9]{2})_([a-zA-Z0-9_.-]{2,10})_([a-zA-Z0-9_.-]{2,10})_INVOICE[.]pdf$/; // "{year}_{month}_{clientId}_{invoiceNumber}_INVOICE";
  // tslint:disable-next-line: max-line-length
  backupNamingConvention: RegExp = /^([0-9]{4})_([0-9]{2})_([a-zA-Z0-9_.-]{2,10})_([a-zA-Z0-9_.-]{2,10})_BACKUP[.](txt|csv|xlsx|xls)$/; // "{year}_{month}_{clientId}_{invoiceNumber}_BACKUP";
  monthLimit;
  yearLimit;
  form: FormGroup;
  uploadFileTypes: string[] = [
    "Invoice",
    "Backup"
  ];

  selectedFileName: string;
  formSubmitted: boolean;

  fileUploadIsInvoice: boolean = false;
  // #endregion

  constructor(
    public sharedDataService: SharedDataService,
    private _fileService: FilePortalService,
    private _fb: FormBuilder) { }

  ngOnInit() {
    const today = new Date();
    this.monthLimit = today.getMonth();
    this.yearLimit = today.getFullYear();

    this.form = this._fb
      .group({
        formFile: [null, Validators.required],
        invoiceNumber: [null],
        invoiceAmount: [null],
        invoicedOn: [null],
        invoiceDueDate: [null]
      });

  }

  public async uploadFile() {
    this.formSubmitted = true;

    if (!this.form.valid) { return; }
    const model = this.form.value;

    const fileName = this.getFileName(model.formFile);

    const isFileConventioned = this.checkFileConventionAndVars(fileName);

    if (!isFileConventioned) {
      // tslint:disable-next-line: max-line-length
      this.errorHandlerComponent.error("When uploading a file, please follow the naming conventions and make sure the year, month, clientId and invoiceNumber are correct. Click Show Full Error to view the conventions.", {
        fullmessage: {
          InvoiceFiles: "YYYY_MM_{clientId}_{InvoiceNumber}_INVOICE.pdf",
          BackupFiles: "YYYY_MM_{clientId}_{InvoiceNumber}_BACKUP.csv|txt|xlsx|xls"
        },
        title: "Incorrect file name"
      });
      return;
    }

    const params: FilePortalService.CheckClientFileExistsParams = {
      fileName: fileName,
      year: this.year,
      month: this.month,
      aviClientId: this.clientId
    };

    try {
      let isFilePresent = await this._fileService.CheckClientFileExists(params).toPromise();

      if (isFilePresent) {
        let userChoice = await this.errorHandlerComponent.info("The file you're uploading already exists. Would like to overwrite it?", {
          buttons: buttontypes.yesorno,
          title: "Replace existing file?"
        });
        if (!userChoice) { return; }
      } else {
        let userChoice = await this.errorHandlerComponent.info("The file you're uploading will not replace any existing client files. Are you sure you want to continue?", {
          buttons: buttontypes.yesorno,
          title: "Add New file?"
        });
        if (!userChoice) { return; }
      }

      let uploadModel = {
        year: this.year,
        month: this.month,
        aviClientId: this.clientId,
        fileType: null,
        formFile: this.fileEl.nativeElement.files[0],
        isInvoice: this.fileUploadIsInvoice,
        invoiceNumber: this.fileUploadIsInvoice ? this.form.get("invoiceNumber").value : null,
        invoiceAmount: this.fileUploadIsInvoice ? this.form.get("invoiceAmount").value : null,
        invoicedOn: this.fileUploadIsInvoice ? this.form.get("invoicedOn").value : null,
        invoiceDueDate: this.fileUploadIsInvoice ? this.form.get("invoiceDueDate").value : null
      };

      this.errorHandlerComponent.notification(`Uploading ${fileName}...`);
      await this._fileService.UploadClientFile(uploadModel).toPromise();

      this.formSubmitted = false;
      this.fileUploadIsInvoice = false;
      this.form.reset();

      this.errorHandlerComponent.notification(`Finished uploading ${fileName}!`);
      this.update.emit(null);
    } catch (ex) {
      this.errorHandlerComponent.error("Server Error: " + ex.error, { fullmessage: ex });
      this.update.emit(null);
    }
  }

  public checkFileConventionAndVars(fileName: string): boolean {
    let result = this.invoiceNamingConvention.test(fileName) || this.backupNamingConvention.test(fileName);
    result = result &&
      fileName.indexOf(this.year.toString(), 0) >= 0 &&
      (fileName.indexOf(this.month.toString(), 4) > 4 && fileName.indexOf(this.month.toString(), 4) < 7) &&
      fileName.indexOf(this.clientId) >= 0;
    return result;
  }

  public getFileName(fileInputValue): string {
    let fullPath = fileInputValue;
    if (fullPath) {
      let startIndex = (fullPath.indexOf('\\') >= 0 ? fullPath.lastIndexOf('\\') : fullPath.lastIndexOf('/'));
      let filename = fullPath.substring(startIndex);
      if (filename.indexOf('\\') === 0 || filename.indexOf('/') === 0) {
        filename = filename.substring(1);
      }
      return filename;
    }
  }

  public onFileSelected(event): void {
    this.selectedFileName = (event.target.files.length > 0) ? event.target.files[0].name : "";
    this.fileUploadIsInvoice = this.selectedFileName.toLowerCase().indexOf("invoice") >= 0;
  
    const invoiceNumber = this.form.get("invoiceNumber");
    const invoiceAmount = this.form.get("invoiceAmount");
    const invoicedOn = this.form.get("invoicedOn");
    const invoiceDueDate = this.form.get("invoiceDueDate");

    if (this.fileUploadIsInvoice) {
      if (this.checkFileConventionAndVars(this.selectedFileName)) {
        // If the file name matches the conventions, then we'll fill in the invoice number
        invoiceNumber.setValue(this.selectedFileName.split("_")[3]);
      }

      invoiceNumber.setValidators([Validators.required]);
      invoiceAmount.setValidators([Validators.required]);
      invoicedOn.setValidators([Validators.required]);
      invoiceDueDate.setValidators([Validators.required]);
    } else {
      invoiceNumber.clearValidators();
      invoiceNumber.reset();
      invoiceAmount.clearValidators();
      invoiceAmount.reset();
      invoicedOn.clearValidators();
      invoicedOn.reset();
      invoiceDueDate.clearValidators();
      invoiceDueDate.reset();
    }
  }
}

